import { forwardRef, useState, useCallback } from "react";
import { bool, number, string, func, object, oneOfType, arrayOf, node } from "prop-types";
import { useTranslation } from "react-i18next";
import { Alert } from "@components";
import { ClickAwayListener } from "@material-ui/core";
import { Input } from "./components/Input";
import { Static } from "./components/Static";
import useStyle from "./TextClearInput.style";
import classnames from "classnames";

export const TextClearInput = forwardRef(({
  value="",
  displayValue,
  onChange,
  onFocus,
  onClick,
  placeholder,
  acceptOnClickAway,
  style,
  confirmOnEnter,
  onAccept,
  focused,
  error,
  errorMessage,
  endAdornment,
  className,
  maxLength,
  minimal,
  noButtons,
  filterChars,
  multiline,
  scaleWidthToContent,
  addInitialBorder,
  preventClickAwayOnElement,
  clickDialogMessageContent,
  clickDialogMessageTitle,
  preventBlurOnRelatedTarget=true,
  lockSpecialCharacters=true,
  inputClassName,
  navigationKey,
  hideAcceptButton,
  tabIndex,
}, ref ) => {
  const { t } = useTranslation();
  const classes = useStyle();

  const [ caretPos, setCaretPos ] = useState(0);
  const [ tooltipVisible, showTooltip ] = useState(false);
  const [ tooltipClosed, setTooltipClosed ] = useState(false);
  
  const focus = (e) => {
    e.stopPropagation();
    if ( tooltipClosed || tooltipVisible ) {
      showTooltip(false);
      setTooltipClosed(false);
      return;
    }
    
    if ( clickDialogMessageTitle ) {
      e.preventDefault();
      showTooltip(true);
      return;
    }
    
    if (!focused && !onClick) {
      const selection = window.getSelection(e);
      setCaretPos(selection.focusOffset);
      if (onFocus) onFocus();
    }
  };
  
  const handleTooltipClose = () => {
    showTooltip(false);
    setTooltipClosed(true);
  };
  
  const handleInputRef = useCallback((r) => {
    if(ref) ref.current = r;
    if(!caretPos || !r) return;
    r.selectionStart = caretPos;
    r.selectionEnd = caretPos;
    setCaretPos(0);
  }, [caretPos]);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    onClick && onClick(e);
  }, [onClick]);

  const handleClickAway = (e) => {
    if(preventClickAwayOnElement && e.target.closest(preventClickAwayOnElement))
      return;
    if (focused && onAccept) onAccept();
  };
  
  const handleBlur = (e) => {
    if ( !e.relatedTarget || !preventBlurOnRelatedTarget)
      handleClickAway(e);
  }

  const handleChange = useCallback((e) => {
    if (!onChange) return;
    /** @notice: characters excluded from module & project names */
    let p = lockSpecialCharacters
      ? e.target.value.replace(/[\\^{}`]/g, "")
      : e.target.value;
    
    if(confirmOnEnter)
      p = p.replace(/\n/g, "");
    
    filterChars?.forEach((char) => {
      p = p.replace(char, "");
    });
    onChange(maxLength ? p.slice(0, maxLength) : p);
  }, [onChange, filterChars, maxLength, lockSpecialCharacters]);

  const handleAccept = useCallback((cancel) => {
    onAccept && onAccept(cancel); // @todo: implement cancel in components using TextClearInput
  }, [onAccept]);
  
  const inputProps = {
    value,
    error,
    errorMessage,
    endAdornment,
    minimal: minimal || noButtons,
    placeholder,
    confirmOnEnter,
    handleChange,
    handleAccept,
    handleClick,
    handleBlur,
    multiline,
    navigationKey,
    tabIndex,
    scaleWidthToContent,
    addInitialBorder,
    hideAcceptButton,
  }

  return (
    <div
      translate="no"
      className={classnames(
        "noDrag",
        classes.root,
        focused && classes.focused,
        error && classes.error,
        minimal && classes.minimal,
        addInitialBorder && classes.addInitialBorder,
        className
      )}
      style={style}
      onClick={focus}
      ref={ref}
    >
      {!focused ? (
        <Static
          value={displayValue || value}
          placeholder={placeholder}
          multiline={multiline}
          className={inputClassName}
          navigationKey={navigationKey}
          tabIndex={tabIndex}
          addInitialBorder={addInitialBorder}
        />
      ) : (
        acceptOnClickAway
         ? <ClickAwayListener mouseEvent="onMouseDown" onClickAway={handleClickAway}>
            <Input ref={handleInputRef} {...inputProps} className={inputClassName} />
          </ClickAwayListener>
         : <Input ref={handleInputRef} {...inputProps} className={inputClassName} />
      )}
      {
        Boolean(clickDialogMessageTitle) &&
        <Alert
          open={ tooltipVisible }
          onAccept={ handleTooltipClose }
          acceptText={t("common.close")}
          title={ clickDialogMessageTitle }
        >
          { clickDialogMessageContent }
        </Alert>
      }
    </div>
  );
});

TextClearInput.propTypes = {
  value: oneOfType([number, string]),
  displayValue: oneOfType([number, string, node]),
  onChange: func,
  onFocus: func,
  onClick: func,
  placeholder: string,
  confirmOnEnter: bool,
  error: bool,
  errorMessage: string,
  onAccept: func,
  focused: bool,
  acceptOnClickAway: bool,
  style: object,
  className: string,
  maxLength: number,
  minimal: bool,
  noButtons: bool,
  multiline: bool,
  lockSpecialCharacters: bool,
  filterChars: arrayOf(string),
  preventClickAwayOnElement: string,
  preventBlurOnRelatedTarget: bool,
  inputClassName: string,
  navigationKey: string,
  noBlurFromEnterKey: bool,
  scaleWidthToContent: bool,
  addInitialBorder: bool,
  hideAcceptButton: bool,
  tabIndex: number,
  endAdornment: node,
  clickDialogMessageTitle: string,
  clickDialogMessageContent: string,
};
